import { CookieService } from 'ngx-cookie-service';
import { ToastrModule } from 'ngx-toastr';
import { ConfirmationService, MessageService } from 'primeng/api';

import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import {
	MSAL_GUARD_CONFIG,
	MSAL_INSTANCE,
	MSAL_INTERCEPTOR_CONFIG,
	MsalBroadcastService,
	MsalGuard,
	MsalGuardConfiguration,
	MsalInterceptor,
	MsalInterceptorConfiguration,
	MsalModule,
	MsalRedirectComponent,
	MsalService,
} from '@azure/msal-angular';
import {
	BrowserCacheLocation,
	InteractionType,
	IPublicClientApplication,
	LogLevel,
	PublicClientApplication,
} from '@azure/msal-browser';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { httpInterceptorProviders } from './core/auth/auth-interceptor';
import { environment } from './core/environments/environment';
import { HandleErrorsInterceptor } from './core/interceptors/handle-errors/handle-errors.interceptor';
import { HttpRequestInterceptor } from './core/interceptors/http-request/http-request.interceptor';
import { SacimModule } from './core/services/sacim.module';
import { MessengerComponent } from './core/websocket/messenger/messenger.component';
import { DevelopmentComponent } from './development/development.component';
import { HomePageComponent } from './home-page/home-page.component';
import { CreditLimitModule } from './modules/credit-limit/credit-limit.module';
import { NotFoundComponent } from './not-found/not-found.component';
import { RestrictComponent } from './restrict/restrict.component';
import { AppPrimengModule } from './shared/app-primeng';
import { LoginFailedComponent } from './core/login-failed/login-failed.component';

export function httpTranslateLoader(http: HttpClient) {
	return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function MSALInstanceFactory(): IPublicClientApplication {
	return new PublicClientApplication({
		auth: {
			clientId: environment.msalConfig.clientId,
			authority: environment.msalConfig.authority,
			redirectUri: environment.msalConfig.redirectUri,
			knownAuthorities: ['login.microsoftonline.com'],
		},
		cache: {
			cacheLocation: BrowserCacheLocation.SessionStorage,
		},
		system: {
			loggerOptions: {
				loggerCallback,
				logLevel: LogLevel.Info,
				piiLoggingEnabled: false,
			},
		},
	});
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
	const protectedResourceMap = new Map<string, Array<string>>();
	protectedResourceMap.set(
		environment.apiConfig.uri,
		environment.apiConfig.scopes,
	);

	return {
		interactionType: InteractionType.Redirect,
		protectedResourceMap,
	};
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		authRequest: {
			scopes: [...environment.apiConfig.scopes],
		},
		loginFailedRoute: '/login-failed',
	};
}

export function loggerCallback(logLevel: LogLevel, message: string) {
	console.log(message);
}

@NgModule({ declarations: [
        AppComponent,
        HomePageComponent,
        NotFoundComponent,
        DevelopmentComponent,
        RestrictComponent,
        MessengerComponent,
        LoginFailedComponent
    ],
    exports: [TranslateModule],
    bootstrap: [AppComponent, MsalRedirectComponent], imports: [FormsModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: httpTranslateLoader,
                deps: [HttpClient],
            },
        }),
        SacimModule,
        CreditLimitModule,
        BrowserModule,
        BrowserAnimationsModule,
        RouterModule,
        AppRoutingModule,
        AppPrimengModule,
        ToastrModule.forRoot({
            timeOut: 10000,
            positionClass: 'toast-top-right',
        }),
        MsalModule], providers: [
        httpInterceptorProviders,
        MessageService,
        CookieService,
        ConfirmationService,
        DatePipe,
        { provide: LOCALE_ID, useValue: 'pt-BR' },
        TranslateModule,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpRequestInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HandleErrorsInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {}
